import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Video,
  UploadCloud,
  CheckCircle,
  Trash2,
  Film,
  Clock,
  FileText,
  HelpCircle,
  Plus,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import {
  apiSlice,
  useDeleteLessonVideoMutation,
  useDeleteLessonMutation,
} from '../../../api/apiSlice';
import { uploadDirect, pollVideoJob } from '../../../lib/uploadDirect';
import Loading from '../../common/Loading';
import ConfirmDialog from '../../common/ConfirmDialog';
import TheoryEditor from '../../course/TheoryEditor';
import QuestionEditor from '../../quiz/QuestionEditor';

const getVideoDuration = (file) =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
      video.remove();
    };
    video.onerror = () => reject(new Error('metadata error'));
    video.src = URL.createObjectURL(file);
  });

// SENIOR UX: инструктор видит только две стадии — «грузим» и «готово».
// Всё что связано со сжатием (queued/processing/failed/heartbeat/progress)
// СПРЯТАНО — это технические детали воркера, которые юзеру нечего знать.
//   • Видео работает сразу после загрузки в R2 (просто в исходном качестве)
//   • Воркер сжимает в фоне и перезаписывает тот же ключ
//   • Если воркер лёг — оригинал останется в R2, студенты смотрят
//   • Когда воркер заработает — BullMQ сам возобновит задачу
//
// Полный gritty-статус компрессии живёт в админке → /admin/video-jobs.
//
// State: 'idle' | 'uploading' | 'ready' | 'upload_failed'
const LessonItem = ({ lesson, courseId, sectionId, onLessonChange }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);
  const abortRef = useRef(null);

  const [state, setState] = useState('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [confirm, setConfirm] = useState(null);

  const [deleteVideo, { isLoading: isDeletingVideo }] = useDeleteLessonVideoMutation();
  const [deleteLesson, { isLoading: isDeletingLesson }] = useDeleteLessonMutation();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setState('uploading');
    setUploadProgress(0);

    abortRef.current = new AbortController();

    try {
      let durationSec = 0;
      try {
        durationSec = await getVideoDuration(file);
      } catch { /* metadata failed, default 0 */ }

      const { videoJobId } = await uploadDirect({
        file,
        kind: 'lesson_video',
        courseId,
        lessonId: lesson._id,
        durationMinutes: durationSec / 60,
        onProgress: (pct) => isMountedRef.current && setUploadProgress(pct),
        signal: abortRef.current.signal,
      });

      if (!isMountedRef.current) return;

      // Refresh course in Redux so lesson.videoKey appears immediately.
      dispatch(apiSlice.util.invalidateTags([{ type: 'Course', id: courseId }]));

      setUploadProgress(100);
      setState('ready');
      toast.success(t('editor.videoUploaded'));
      // Через 2с возвращаемся в idle — UI не накапливает зелёные галки.
      setTimeout(() => isMountedRef.current && setState('idle'), 2000);

      // Сжатие крутится в фоне БЕЗ UI. Тихо инвалидируем кэш курса когда
      // воркер закончит — на случай если что-то в lesson обновилось.
      // Ошибки полинга молча игнорим: видео всё равно работает в R2.
      if (videoJobId) {
        pollVideoJob({ jobId: videoJobId, intervalMs: 5000 })
          .then(() => {
            if (!isMountedRef.current) return;
            dispatch(apiSlice.util.invalidateTags([{ type: 'Course', id: courseId }]));
          })
          .catch(() => { /* silent — admin видит детали в видео-джобах */ });
      }
    } catch (err) {
      if (isMountedRef.current) {
        const msg = err?.data?.message || err?.message || t('editor.uploadError');
        toast.error(msg);
        setState('upload_failed');
        setTimeout(() => isMountedRef.current && setState('idle'), 3000);
      }
    }
  };

  const handleDeleteVideo = async () => {
    try {
      await deleteVideo({ courseId, lessonId: lesson._id }).unwrap();
      toast.success(t('editor.videoDeleted'));
    } catch (err) {
      toast.error(err?.data?.message || t('editor.deleteError'));
    } finally {
      setConfirm(null);
    }
  };

  const handleDeleteLesson = async () => {
    try {
      await deleteLesson({ courseId, sectionId, lessonId: lesson._id }).unwrap();
      toast.success(t('editor.lessonDeleted'));
    } catch (err) {
      toast.error(err?.data?.message || t('editor.deleteError'));
    } finally {
      setConfirm(null);
    }
  };

  if (!lesson || !lesson._id) return null;

  const lessonType = lesson.type || 'video';
  const hasVideo = Boolean(lesson.videoKey);
  const isBusy = state === 'uploading';

  // Helper-иконка слева — меняется в зависимости от типа.
  const HeadIcon =
    lessonType === 'theory' ? FileText : lessonType === 'quiz' ? HelpCircle : hasVideo ? Film : Video;
  const headIconColor =
    lessonType === 'theory'
      ? 'text-blue-500'
      : lessonType === 'quiz'
      ? 'text-purple-500'
      : hasVideo
      ? 'text-emerald-500'
      : 'text-slate-400';

  // Quiz helpers (живут в lesson.quizQuestions, контролируется через onLessonChange).
  const quizQuestions = Array.isArray(lesson.quizQuestions) ? lesson.quizQuestions : [];
  const setQuizQuestions = (next) => onLessonChange('quizQuestions', next);
  const setQuestion = (idx, q) =>
    setQuizQuestions(quizQuestions.map((qq, i) => (i === idx ? q : qq)));
  const addQuestion = () =>
    setQuizQuestions([
      ...quizQuestions,
      {
        text: '',
        options: [{ text: '' }, { text: '' }],
        correctOptionIndex: 0,
        explanation: '',
      },
    ]);
  const removeQuestion = (idx) =>
    setQuizQuestions(quizQuestions.filter((_, i) => i !== idx));
  const moveQuestion = (idx, dir) => {
    const next = [...quizQuestions];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setQuizQuestions(next);
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="flex h-16 w-16 sm:h-20 sm:w-20 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800">
          <HeadIcon size={26} className={headIconColor} />
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          {/* Тип урока — селектор (необязательное расширение видео-курса теорией / квизом) */}
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Тип:
            </label>
            <select
              value={lessonType}
              onChange={(e) => onLessonChange('type', e.target.value)}
              className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="video">Видео</option>
              <option value="theory">Теория</option>
              <option value="quiz">Квиз (гейт)</option>
            </select>
            {lessonType === 'quiz' && (
              <span className="inline-flex items-center gap-1 text-[11px] text-purple-700 dark:text-purple-300">
                Минимум для прохода:
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={Number(lesson.passingScore) || 60}
                  onChange={(e) => {
                    const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                    onLessonChange('passingScore', v);
                  }}
                  className="w-14 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                %
              </span>
            )}
          </div>

          <input
            type="text"
            value={lesson.title || ''}
            onChange={(e) => onLessonChange('title', e.target.value)}
            placeholder={t('editor.lessonTitlePlaceholder')}
            className="w-full bg-transparent border-b border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-indigo-500 focus:outline-none font-medium text-slate-900 dark:text-white py-1"
          />
          <textarea
            value={lesson.description || ''}
            onChange={(e) => onLessonChange('description', e.target.value)}
            placeholder={t('editor.lessonDescPlaceholder')}
            rows={2}
            className="w-full bg-transparent text-sm text-slate-600 dark:text-slate-400 border-b border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-indigo-500 focus:outline-none py-1 resize-none"
          />

          {lessonType === 'video' && (
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400 pt-1">
              <span className="inline-flex items-center gap-1">
                <Clock size={12} />
                {Math.round(lesson.durationMinutes) || 0} {t('editor.minShort')}
              </span>
              {lesson.originalVideoName && (
                <span className="inline-flex items-center gap-1 truncate max-w-[200px]">
                  <CheckCircle size={12} className="text-emerald-500" />
                  <span className="truncate" title={lesson.originalVideoName}>{lesson.originalVideoName}</span>
                </span>
              )}
            </div>
          )}

          {/* Theory editor (только при lesson.type === 'theory') */}
          {lessonType === 'theory' && (
            <div className="pt-2">
              <TheoryEditor
                value={lesson.theoryContent || ''}
                onChange={(v) => onLessonChange('theoryContent', v)}
              />
              <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                Markdown · поддерживает заголовки, **жирный**, _курсив_, списки и
                LaTeX-формулы ($a^2 + b^2 = c^2$, $$...$$). Вставка из Word/Google Docs
                автоматически приводится к чистому тексту.
              </p>
            </div>
          )}

          {/* Quiz editor (только при lesson.type === 'quiz') */}
          {lessonType === 'quiz' && (
            <div className="pt-2 space-y-3">
              {quizQuestions.length === 0 && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Добавьте хотя бы один вопрос, чтобы квиз сработал как гейт.
                </p>
              )}
              {quizQuestions.map((q, i) => (
                <QuestionEditor
                  key={i}
                  index={i}
                  total={quizQuestions.length}
                  question={q}
                  onChange={(qq) => setQuestion(i, qq)}
                  onMoveUp={() => moveQuestion(i, -1)}
                  onMoveDown={() => moveQuestion(i, +1)}
                  onRemove={() => removeQuestion(i)}
                />
              ))}
              <button
                type="button"
                onClick={addQuestion}
                disabled={quizQuestions.length >= 30}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 py-3 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" /> Добавить вопрос
              </button>
            </div>
          )}

          {/* Только две стадии видимы для инструктора:
                • Загрузка с прогрессом (что-то происходит на R2)
                • Готово ✓ (зелёная галка на 2 секунды)
              Сжатие — фоновая операция, статус не показываем. Если что-то
              сломается в сжатии — видео всё равно играется (R2 хранит оригинал).
              Подробности по сжатию — в админ-панели. */}
          {lessonType === 'video' && state === 'uploading' && (
            <div className="pt-2">
              <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                <span>{t('editor.uploading')}</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-600 transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          {lessonType === 'video' && state === 'ready' && (
            <div className="pt-2 inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
              <CheckCircle size={12} />
              {t('editor.videoReady')}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 shrink-0">
          {lessonType === 'video' && (
            <>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isBusy}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                title={hasVideo ? t('editor.replaceVideo') : t('editor.uploadVideo')}
              >
                <UploadCloud size={14} />
                {hasVideo ? t('editor.replaceVideo') : t('editor.uploadVideo')}
              </button>

              {hasVideo && (
                <button
                  type="button"
                  onClick={() => setConfirm('video')}
                  disabled={isDeletingVideo || isBusy}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/30 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-950/50 disabled:opacity-50"
                >
                  <Trash2 size={14} />
                  {t('editor.deleteVideo')}
                </button>
              )}
            </>
          )}

          <button
            type="button"
            onClick={() => setConfirm('lesson')}
            disabled={isDeletingLesson || isBusy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/50 disabled:opacity-50"
          >
            <Trash2 size={14} />
            {t('editor.deleteLesson')}
          </button>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleVideoUpload}
          className="hidden"
          accept="video/*"
          disabled={isBusy}
        />
      </div>

      <ConfirmDialog
        open={confirm === 'video'}
        title={t('editor.deleteVideoConfirmTitle')}
        description={t('editor.deleteVideoConfirmDesc')}
        confirmLabel={t('editor.deleteVideo')}
        destructive
        loading={isDeletingVideo}
        onConfirm={handleDeleteVideo}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'lesson'}
        title={t('editor.deleteLessonConfirmTitle')}
        description={t('editor.deleteLessonConfirmDesc')}
        confirmLabel={t('editor.deleteLesson')}
        destructive
        loading={isDeletingLesson}
        onConfirm={handleDeleteLesson}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default LessonItem;
